import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { FilePlus, FileText } from "lucide-react";

import { Button } from "@/components/ui/button";

import { VoltDataTable, type VoltColumn } from "../ui/VoltDataTable";
import { VoltEmpty } from "../ui/VoltEmpty";
import { VoltPageHeader } from "../ui/VoltPageHeader";
import { VoltStatusBadge } from "../ui/VoltStatusBadge";
import {
  buddyFor,
  DRAFT_STATUS,
  draftPath,
  draftTitle,
  formatDate,
  isRunning,
} from "./draftText";
import { TrafficLightDot } from "./TrafficLight";
import { WorkshopError } from "./WorkshopError";
import { WorkshopFrame, type WorkshopMeta } from "./WorkshopFrame";
import { WorkshopTabs } from "./WorkshopTabs";
import { errorText, workshopApi } from "./workshopApi";
import type { Draft } from "./types";

export const WorkshopListPage = () => (
  <WorkshopFrame>{(meta) => <DraftList meta={meta} />}</WorkshopFrame>
);

const DraftList = ({
  meta: { status, committees },
}: {
  meta: WorkshopMeta;
}) => {
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only the newest answer counts; a success clears an earlier error.
  const seq = useRef(0);
  const load = useCallback(() => {
    const mine = ++seq.current;
    workshopApi
      .drafts()
      .then((d) => {
        if (mine !== seq.current) return;
        setDrafts(d);
        setError(null);
      })
      .catch((e) => mine === seq.current && setError(errorText(e)));
  }, []);

  useEffect(() => {
    const counter = seq;
    load();
    return () => {
      counter.current++;
    };
  }, [load]);

  // Running drafts refresh themselves, as in the old app.
  const running = drafts?.some(isRunning) ?? false;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);

  const fraktionView = !!status?.admin_sicht;
  const canCreate = !!status?.konfiguriert && status.verbleibend !== 0;

  const columns: VoltColumn<Draft>[] = [
    {
      key: "title",
      header: "Thema",
      cell: (d) => <span className="font-medium">{draftTitle(d)}</span>,
      className: "max-w-sm whitespace-normal",
    },
    { key: "art", header: "Art", cell: (d) => d.art, hideOnMobile: true },
    {
      key: "committee",
      header: "Gremium",
      cell: (d) => d.gremium ?? "—",
      hideOnMobile: true,
    },
    ...(fraktionView
      ? [
          {
            key: "buddy",
            header: "Buddy",
            cell: (d: Draft) => buddyFor(committees, d.gremium),
            hideOnMobile: true,
          },
          {
            key: "creator",
            header: "Erstellt von",
            cell: (d: Draft) => d.ersteller ?? "—",
            hideOnMobile: true,
          },
        ]
      : []),
    {
      key: "outlook",
      header: "Aussicht",
      cell: (d) =>
        d.status === "fertig" && d.score !== null ? (
          <span className="flex items-center gap-2 tabular-nums">
            <TrafficLightDot light={d.ampel} />
            {d.score} %
          </span>
        ) : (
          <VoltStatusBadge status={d.status} map={DRAFT_STATUS} />
        ),
    },
    {
      key: "date",
      header: "Datum",
      cell: (d) => formatDate(d.erstellt_am),
      align: "right",
      hideOnMobile: true,
    },
  ];

  return (
    <>
      <WorkshopTabs />
      <VoltPageHeader
        title={fraktionView ? "Entwürfe der Fraktion" : "Deine Entwürfe"}
        description={
          status && status.verbleibend !== null
            ? status.verbleibend > 0
              ? `Noch ${status.verbleibend} von ${status.kontingent} Entwürfen in diesem Monat.`
              : `Kontingent aufgebraucht (${status.kontingent} Entwürfe im Monat). Ab dem Ersten des nächsten Monats geht es weiter.`
            : "Anträge und Anfragen"
        }
        action={
          canCreate ? (
            <Button asChild>
              <Link to="/workshop/new">
                <FilePlus />
                Neuer Entwurf
              </Link>
            </Button>
          ) : (
            <Button disabled>
              <FilePlus />
              Neuer Entwurf
            </Button>
          )
        }
      />
      {error && <WorkshopError>{error}</WorkshopError>}
      <VoltDataTable
        rows={drafts ?? []}
        columns={columns}
        rowKey={(d) => d.id}
        rowHref={(d) => draftPath(d.id)}
        loading={drafts === null && !error}
        empty={
          <VoltEmpty
            icon={FileText}
            title="Noch nichts da"
            description="Oben rechts auf „Neuer Entwurf“ – der erste steht in ein paar Minuten."
          />
        }
      />
    </>
  );
};
